import { useEffect, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { m } from "#/paraglide/messages";
import { useMarket } from "#/contexts/MarketContext";
import {
  useGetProductionLaboOrders,
  productionLaboOrdersKey,
} from "#/lib/api/production-labo";
import { useAddDelayNote } from "#/lib/api/delay-note";
import { useRequiredTenantId } from "#/lib/api/common";
import { PaginatedSearchSchema } from "#/lib/schemas/paginated-search";
import { ProductionLaboPage } from "#/components/pages/production-labo/production-labo-page";

const SORT_STORAGE_KEY = "productionLabo.sort";

export const Route = createFileRoute("/$tenantCode/$market/production-labo")({
  beforeLoad: () => ({
    getTitle: () => m["resources.productionLabo.name"](),
  }),
  validateSearch: PaginatedSearchSchema,
  component: ProductionLaboRoute,
});

function ProductionLaboRoute() {
  const market = useMarket();
  const { page, perPage, sort } = Route.useSearch();

  const [initialSort] = useState<string | undefined>(() => {
    const raw = sessionStorage.getItem(SORT_STORAGE_KEY);
    return raw !== null && raw !== "" ? raw : undefined;
  });
  const effectiveSort = sort ?? initialSort;

  useEffect(() => {
    if (sort !== undefined && sort !== "") {
      sessionStorage.setItem(SORT_STORAGE_KEY, sort);
    }
  }, [sort]);

  const [sortField, sortDirection] = (() => {
    if (effectiveSort === undefined || effectiveSort === "") {
      return [undefined, undefined];
    }
    const parts = effectiveSort.split(".");
    if (parts.length !== 2) return [undefined, undefined];
    const [field, direction] = parts;
    if (direction !== "asc" && direction !== "desc") {
      return [undefined, undefined];
    }
    return [field, direction.toUpperCase()];
  })();

  const { data, isLoading } = useGetProductionLaboOrders({
    market,
    page,
    itemsPerPage: perPage,
    sortField,
    sortDirection,
  });

  const tenantId = useRequiredTenantId();
  const { handleAddNote } = useAddDelayNote([
    productionLaboOrdersKey(tenantId),
  ]);

  const rows = data?.member ?? [];
  const total = Number(data?.totalItems ?? 0);
  const pageCount = Math.ceil(total / perPage);

  return (
    <ProductionLaboPage
      data={rows}
      total={total}
      pageCount={pageCount}
      pageSize={perPage}
      isLoading={isLoading}
      initialSort={initialSort}
      onAddNote={handleAddNote}
    />
  );
}
